/**
 * Custom hook for Device Management
 * Handles state and API calls for device lifecycle management
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import api from '@/services/api/client';
import { Device, DeviceHistory, DeviceStats, DeviceStatus } from '@/types/device';

/** Matchar backend deviceSearchNormalize (PDF/Excel osynliga tecken). */
function normalizeDeviceSearchInput(raw: string): string {
  let s = raw.trim();
  s = s.replace(/[\u200B-\u200F\u202A-\u202E\u2060\uFEFF\u00AD]/g, '');
  s = s.replace(/\u00A0/g, ' ');
  s = s.replace(/\s+/g, ' ');
  return s.trim();
}

export const useDeviceManagement = () => {

  // State
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | ''>('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceHistory, setDeviceHistory] = useState<DeviceHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Debounce sökning – 300ms fördröjning
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const devicesQueryKey = ['device-management', 'devices', debouncedSearch, statusFilter];

  const { data: devices = [], isLoading: devicesLoading, refetch: refetchDevices } = useQuery(
    devicesQueryKey,
    async () => {
      const params = new URLSearchParams();
      const q = normalizeDeviceSearchInput(debouncedSearch);
      if (q.length > 0) params.append('search', q);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '0'); // 0 = ingen begränsning, hämta alla
      params.append('includeSubOrgs', 'true');

      const response = await api.get(`/api/device-management/devices?${params}`);
      if (response.data.success) {
        return response.data.data as Device[];
      }
      return [];
    },
    { staleTime: 30 * 1000 }
  );

  const { data: stats = null, refetch: refetchStats } = useQuery(
    ['device-management', 'stats'],
    async () => {
      const response = await api.get('/api/device-management/stats');
      if (response.data.success) {
        return response.data.data as DeviceStats;
      }
      return null;
    },
    { staleTime: 30 * 1000 }
  );

  const loading = devicesLoading;

  const fetchDeviceHistory = useCallback(async (imei: string) => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/api/device-management/devices/${imei}/history`);
      if (response.data.success) {
        setDeviceHistory(response.data.data);
      }
    } catch {
      // Error fetching device history - handled silently
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load history when device is selected
  useEffect(() => {
    if (selectedDevice) {
      fetchDeviceHistory(selectedDevice.imei);
    }
  }, [selectedDevice, fetchDeviceHistory]);

  // Actions
  const handleStatusChange = async (imei: string, newStatus: DeviceStatus) => {
    try {
      await api.put(`/api/device-management/devices/${imei}/status`, { status: newStatus });
      refetchDevices();
      refetchStats();
      if (selectedDevice?.imei === imei) {
        fetchDeviceHistory(imei);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Fel vid statusändring');
    }
  };
  
  const handleUnmount = async (imei: string, keepWithCustomer: boolean) => {
    try {
      await api.post(`/api/device-management/devices/${imei}/unmount`, { keepWithCustomer });
      refetchDevices();
      refetchStats();
      if (selectedDevice?.imei === imei) {
        fetchDeviceHistory(imei);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Fel vid demontering');
    }
  };

  const handleAssignOrganization = async (imei: string, organizationId: string) => {
    try {
      const response = await api.put(`/api/device-management/devices/${imei}`, { organizationId });
      if (response.data.success) {
        setSelectedDevice(response.data.data);
        refetchDevices();
        refetchStats();
        fetchDeviceHistory(imei);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Fel vid tilldelning av organisation');
    }
  };

  const refreshData = () => {
    refetchDevices();
    refetchStats();
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    refetchDevices();
    refetchStats();
  };

  return {
    // Data
    devices,
    stats,
    loading,
    selectedDevice,
    deviceHistory,
    historyLoading,
    
    // Filters
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    
    // Selection
    setSelectedDevice,
    
    // Modal
    showImportModal,
    setShowImportModal,
    
    // Actions
    handleStatusChange,
    handleUnmount,
    handleAssignOrganization,
    refreshData,
    handleImportSuccess,
  };
};
